import { type ErrorType, apiRequest } from "../api";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WebhookInfo {
  callbackUrl: string;
  verifyToken: string;
  fields: string[];
  note?: string;
}

export interface ActivatePayload {
  code: string;
  wabaId: string;
  phoneNumberId: string;
  businessId?: string;
  name?: string;
}

export interface ManualPayload {
  wabaId: string;
  phoneNumberId: string;
  accessToken: string;
  businessId?: string;
  name?: string;
  appSecret?: string;
}

export interface WhatsAppChannelResult {
  _id: string;
  name: string;
  wabaId?: string;
  phoneNumberId?: string;
  businessId?: string;
  displayPhoneNumber?: string;
  type: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WhatsAppStatus {
  configured: boolean;
  wabaId?: string;
  phoneNumberId?: string;
  businessId?: string;
  name?: string;
  displayPhoneNumber?: string;
}

export interface ActivateResponse {
  success: boolean;
  channel: WhatsAppChannelResult;
}

export type TemplateCategory = "MARKETING" | "UTILITY" | "AUTHENTICATION";
export type TemplateStatus = "APPROVED" | "PENDING" | "REJECTED";

export interface TemplateComponent {
  type: "HEADER" | "BODY" | "FOOTER" | "BUTTONS";
  text?: string;
  /** Exemplos de preenchimento das variáveis {{1}}, {{2}}... — obrigatório na Meta quando há variáveis. */
  example?: { body_text?: string[][] };
  buttons?: Array<{ type: string; text: string; url?: string; phone_number?: string }>;
}

export interface WhatsAppTemplate {
  id: string;
  name: string;
  status: TemplateStatus;
  category: TemplateCategory;
  language: string;
  components: TemplateComponent[];
  /** Motivo da rejeição, quando a Meta fornece um (status = REJECTED). */
  rejected_reason?: string;
}

export interface CreateTemplatePayload {
  name: string;
  category: TemplateCategory;
  language: string;
  components: TemplateComponent[];
}

export interface UpdateTemplatePayload {
  category?: TemplateCategory;
  components: TemplateComponent[];
}

export interface SetTicketTemplateResponse {
  success: boolean;
  ticketTemplateName: string;
  ticketTemplateLang: string;
}

// ─── Templates de plataforma (não-Meta, texto livre com janela aberta) ────────

export type PlatformTemplateKind = "onboarding" | "ticket_created" | "out_of_hours" | "custom";

export interface PlatformTemplate {
  _id: string;
  kind: PlatformTemplateKind;
  label: string;
  body: string;
  active: boolean;
}

export interface PlatformTemplatePayload {
  label?: string;
  body?: string;
  active?: boolean;
}

// ─── Official Business Account (OBA) ──────────────────────────────────────────

export type OBAStatus = "NOT_STARTED" | "IN_REVIEW" | "APPROVED" | "REJECTED" | "SUSPENDED" | string;

export interface OBAStatusResponse {
  obaStatus: OBAStatus | null;
}

// ─── Grupos ────────────────────────────────────────────────────────────────────

export type GroupJoinApprovalMode = "auto_approve" | "approval_required";

export interface WhatsAppGroup {
  _id: string;
  groupId: string;
  phoneNumberId: string;
  subject: string;
  description?: string;
  joinApprovalMode: GroupJoinApprovalMode;
  createdAt: string;
  updatedAt: string;
}

export interface WhatsAppGroupInfo {
  id: string;
  subject?: string;
  description?: string;
  join_approval_mode?: GroupJoinApprovalMode;
  suspended?: boolean;
  creation_timestamp?: number;
  total_participant_count?: number;
  participants?: Array<{ user: string; status?: string }>;
}

export interface CreateGroupPayload {
  subject: string;
  description?: string;
  join_approval_mode?: GroupJoinApprovalMode;
}

export interface UpdateGroupPayload {
  subject?: string;
  description?: string;
}

// ─── Custo de mensagens (dashboard) ───────────────────────────────────────────

export type BillingCategory = "marketing" | "utility" | "authentication" | "service";

export interface CostSettings {
  marketing: number;
  utility: number;
  authentication: number;
  serviceProjected: number;
  isCustom: boolean;
}

export interface CostUsageRow {
  category: BillingCategory;
  count: number;
  currentRate: number;
  currentSubtotal: number;
  projectedRate: number;
  projectedSubtotal: number;
}

export interface CostUsageSummary {
  from: string;
  to: string;
  totalMessages: number;
  currentTotal: number;
  projectedTotal: number;
  increase: number;
  increasePct: number | null;
  rows: CostUsageRow[];
  settings: CostSettings;
  pricingChangeDate: string;
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const whatsappService = {
  async status(): Promise<[WhatsAppStatus | null, ErrorType | null]> {
    return apiRequest<WhatsAppStatus>({
      loaderKey: "whatsappStatus",
      method: "get",
      url: "/app/whatsapp/status",
    });
  },

  async webhookInfo(): Promise<[WebhookInfo | null, ErrorType | null]> {
    return apiRequest<WebhookInfo>({
      loaderKey: "whatsappWebhookInfo",
      method: "get",
      url: "/app/whatsapp/webhook-info",
    });
  },

  async activate(
    payload: ActivatePayload
  ): Promise<[ActivateResponse | null, ErrorType | null]> {
    return apiRequest<ActivateResponse>({
      loaderKey: "whatsappActivate",
      method: "post",
      url: "/app/whatsapp/activate",
      data: payload,
    });
  },

  async manual(
    payload: ManualPayload
  ): Promise<[ActivateResponse | null, ErrorType | null]> {
    return apiRequest<ActivateResponse>({
      loaderKey: "whatsappManual",
      method: "post",
      url: "/app/whatsapp/manual",
      data: payload,
    });
  },

  async listTemplates(): Promise<[WhatsAppTemplate[] | null, ErrorType | null]> {
    return apiRequest<WhatsAppTemplate[]>({
      loaderKey: "whatsappTemplates",
      method: "get",
      url: "/app/whatsapp/templates",
    });
  },

  async listPlatformTemplates(): Promise<[PlatformTemplate[] | null, ErrorType | null]> {
    return apiRequest<PlatformTemplate[]>({
      loaderKey: "platformTemplates",
      method: "get",
      url: "/app/whatsapp/platform-templates",
    });
  },

  async createPlatformTemplate(
    payload: PlatformTemplatePayload
  ): Promise<[PlatformTemplate | null, ErrorType | null]> {
    return apiRequest<PlatformTemplate>({
      loaderKey: "createPlatformTemplate",
      method: "post",
      url: "/app/whatsapp/platform-templates",
      data: payload,
    });
  },

  async updatePlatformTemplate(
    id: string,
    payload: PlatformTemplatePayload
  ): Promise<[PlatformTemplate | null, ErrorType | null]> {
    return apiRequest<PlatformTemplate>({
      loaderKey: "updatePlatformTemplate",
      method: "put",
      url: `/app/whatsapp/platform-templates/${id}`,
      data: payload,
    });
  },

  async deletePlatformTemplate(id: string): Promise<[{ success: boolean } | null, ErrorType | null]> {
    return apiRequest<{ success: boolean }>({
      loaderKey: "deletePlatformTemplate",
      method: "delete",
      url: `/app/whatsapp/platform-templates/${id}`,
    });
  },

  async createTemplate(
    payload: CreateTemplatePayload
  ): Promise<[WhatsAppTemplate | null, ErrorType | null]> {
    return apiRequest<WhatsAppTemplate>({
      loaderKey: "whatsappCreateTemplate",
      method: "post",
      url: "/app/whatsapp/templates",
      data: payload,
    });
  },

  async templateStatus(
    id: string
  ): Promise<[WhatsAppTemplate | null, ErrorType | null]> {
    return apiRequest<WhatsAppTemplate>({
      loaderKey: "whatsappTemplateStatus",
      method: "get",
      url: `/app/whatsapp/templates/${id}/status`,
    });
  },

  async updateTemplate(
    id: string,
    payload: UpdateTemplatePayload
  ): Promise<[WhatsAppTemplate | null, ErrorType | null]> {
    return apiRequest<WhatsAppTemplate>({
      loaderKey: "whatsappUpdateTemplate",
      method: "put",
      url: `/app/whatsapp/templates/${id}`,
      data: payload,
    });
  },

  async deleteTemplate(
    id: string,
    opts?: { localOnly?: boolean }
  ): Promise<[{ success: boolean; metaDeleted: boolean } | null, ErrorType | null]> {
    return apiRequest<{ success: boolean; metaDeleted: boolean }>({
      loaderKey: "whatsappDeleteTemplate",
      method: "delete",
      url: `/app/whatsapp/templates/${id}`,
      params: opts?.localOnly ? { localOnly: "true" } : undefined,
    });
  },

  async setTicketTemplate(
    name: string,
    language?: string
  ): Promise<[SetTicketTemplateResponse | null, ErrorType | null]> {
    return apiRequest<SetTicketTemplateResponse>({
      loaderKey: "whatsappSetTicketTemplate",
      method: "put",
      url: "/app/whatsapp/ticket-template",
      data: { name, ...(language ? { language } : {}) },
    });
  },

  async getCostSettings(): Promise<[CostSettings | null, ErrorType | null]> {
    return apiRequest<CostSettings>({
      loaderKey: "whatsappCostSettings",
      method: "get",
      url: "/app/whatsapp/cost/settings",
    });
  },

  async updateCostSettings(
    payload: Partial<Pick<CostSettings, "marketing" | "utility" | "authentication" | "serviceProjected">>
  ): Promise<[CostSettings | null, ErrorType | null]> {
    return apiRequest<CostSettings>({
      loaderKey: "whatsappUpdateCostSettings",
      method: "put",
      url: "/app/whatsapp/cost/settings",
      data: payload,
    });
  },

  async getCostSummary(from?: string, to?: string): Promise<[CostUsageSummary | null, ErrorType | null]> {
    return apiRequest<CostUsageSummary>({
      loaderKey: "whatsappCostSummary",
      method: "get",
      url: "/app/whatsapp/cost/summary",
      params: { ...(from ? { from } : {}), ...(to ? { to } : {}) },
    });
  },

  async getObaStatus(): Promise<[OBAStatusResponse | null, ErrorType | null]> {
    return apiRequest<OBAStatusResponse>({
      loaderKey: "whatsappObaStatus",
      method: "get",
      url: "/app/whatsapp/oba-status",
    });
  },

  async listGroups(): Promise<[WhatsAppGroup[] | null, ErrorType | null]> {
    return apiRequest<WhatsAppGroup[]>({
      loaderKey: "whatsappGroups",
      method: "get",
      url: "/app/whatsapp/groups",
    });
  },

  async createGroup(payload: CreateGroupPayload): Promise<[WhatsAppGroup | null, ErrorType | null]> {
    return apiRequest<WhatsAppGroup>({
      loaderKey: "whatsappCreateGroup",
      method: "post",
      url: "/app/whatsapp/groups",
      data: payload,
    });
  },

  async getGroup(groupId: string): Promise<[WhatsAppGroupInfo | null, ErrorType | null]> {
    return apiRequest<WhatsAppGroupInfo>({
      loaderKey: "whatsappGroupInfo",
      method: "get",
      url: `/app/whatsapp/groups/${groupId}`,
    });
  },

  async updateGroup(
    groupId: string,
    payload: UpdateGroupPayload
  ): Promise<[WhatsAppGroup | null, ErrorType | null]> {
    return apiRequest<WhatsAppGroup>({
      loaderKey: "whatsappUpdateGroup",
      method: "put",
      url: `/app/whatsapp/groups/${groupId}`,
      data: payload,
    });
  },

  async deleteGroup(groupId: string): Promise<[{ success: boolean } | null, ErrorType | null]> {
    return apiRequest<{ success: boolean }>({
      loaderKey: "whatsappDeleteGroup",
      method: "delete",
      url: `/app/whatsapp/groups/${groupId}`,
    });
  },

  async getGroupInviteLink(groupId: string): Promise<[{ invite_link?: string } | null, ErrorType | null]> {
    return apiRequest<{ invite_link?: string }>({
      loaderKey: "whatsappGroupInviteLink",
      method: "get",
      url: `/app/whatsapp/groups/${groupId}/invite-link`,
    });
  },

  async resetGroupInviteLink(groupId: string): Promise<[{ invite_link?: string } | null, ErrorType | null]> {
    return apiRequest<{ invite_link?: string }>({
      loaderKey: "whatsappResetGroupInviteLink",
      method: "post",
      url: `/app/whatsapp/groups/${groupId}/invite-link/reset`,
    });
  },

  async removeGroupParticipants(
    groupId: string,
    participants: string[]
  ): Promise<[{ success: boolean } | null, ErrorType | null]> {
    return apiRequest<{ success: boolean }>({
      loaderKey: "whatsappRemoveGroupParticipants",
      method: "delete",
      url: `/app/whatsapp/groups/${groupId}/participants`,
      data: { participants },
    });
  },
};
